<template>
  <v-container fluid class="pa-2 bg-grey-darken-4">
    <v-card class="mx-auto" max-width="800" variant="elevated" theme="dark">
  
      <v-card-item class="pa-4">
        <div class="d-flex justify-space-between align-start flex-wrap ga-2">
          <div>
            <div class="d-flex align-center ga-2">
              <h1 class="text-h6 font-weight-bold text-white">{{ portfolioData.name }}</h1>
              <v-btn :loading="loading" icon="mdi-refresh" variant="text" size="small" @click="refreshPnL"></v-btn>
            </div>
            <span class="text-caption text-grey-lighten-1">{{ portfolioData.status }}</span>
          </div>
  
  
          <div class="d-flex align-center ga-2">
            <div class="d-flex align-center justify-end ga-2">
              <p class="text-h6 font-weight-bold" :class="getColorClass(portfolioData.pnl)">
                ₹{{ portfolioData.pnl.toLocaleString('en-IN') }}
              </p>
  
            </div>
            <div v-if="lastUpdated" class="text-caption text-grey-lighten-2 mt-1">
              Last updated: {{ lastUpdated }}
            </div>
          </div>
        </div>
      </v-card-item>
  
      <v-divider />
  
      <div class="pa-4">
        <NiftyLevel v-slot="{ currentLevel }">
          <div v-if="currentLevel !== null && portfolioData.startLevel.value > 0">
            <span class="text-grey-lighten-1">Start Level: </span>
            <span class="font-weight-medium">{{ portfolioData.startLevel.value.toLocaleString('en-IN') }}</span>
            <span :class="getColorClass(currentLevel - portfolioData.startLevel.value)" class="font-weight-medium ml-1">
              [{{ (currentLevel - portfolioData.startLevel.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })
              }}]
            </span>
          </div>
        </NiftyLevel>
      </div>
      <v-expansion-panels variant="accordion" class="pa-2">
        <v-expansion-panel v-for="(strategy, i) in portfolioData.strategies" :key="i"
          class="bg-grey-darken-3 rounded-lg mb-2" elevation="0">
          <v-expansion-panel-title class="py-3">
            <v-row no-gutters class="align-center">
              <v-col cols="12" sm="6">
                <div class="font-weight-bold">{{ strategy.name }}</div>
                <div class="text-caption text-grey-lighten-1">{{ strategy.type }}</div>
              </v-col>
              <v-col cols="12" sm="6" class="text-sm-right font-weight-bold text-h6 mt-2 mt-sm-0"
                :class="getColorClass(strategy.pnl)">
                ₹{{ (strategy.pnl || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                }}
              </v-col>
            </v-row>
          </v-expansion-panel-title>
  
          <v-expansion-panel-text class="pa-2">
            <div v-for="(pos, j) in strategy.positions" :key="j" class="bg-grey-darken-2 rounded pa-3 mb-2">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="font-weight-bold text-body-1">{{ pos.ticker }}</span>
                <v-chip :color="pos.type === 'Buy' ? 'blue' : 'pink'" size="small" label variant="flat">{{ pos.type
                  }}</v-chip>
              </div>
              <v-divider class="mb-3 border-opacity-50" />
              <v-row no-gutters class="text-body-2">
                <v-col cols="6" sm="3" class="mb-2"><span class="text-grey-lighten-1">Qty:</span> {{ pos.qty }}</v-col>
                <v-col cols="6" sm="3" class="mb-2"><span class="text-grey-lighten-1">Buy:</span> {{ pos.buyPrice
                  }}</v-col>
                <v-col cols="6" sm="3" class="mb-2"><span class="text-grey-lighten-1">LTP:</span> {{ pos.ltp }}</v-col>
                <v-col cols="6" sm="3" class="mb-2 font-weight-bold" :class="getColorClass(pos.pnl)">PNL: {{
                  (pos.pnl || 0).toLocaleString('en-IN') }}</v-col>
              </v-row>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
    <div v-if="motivationalImageUrl" class="motivation-card mx-auto" :class="{ 'is-visible': isImageVisible }">
      <img class="responsive-image" :src="motivationalImageUrl" alt="Motivational Quote">
    </div>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import NiftyLevel from '@/components/trades/NiftyLevel.vue'; // 👈 --- IMPORT THE NEW COMPONENT

// --- Reactive Data State ---
const portfolioData = ref({
  name: 'Loading Portfolio...',
  status: '',
  pnl: 0,
  // nifty object is no longer needed here
  startLevel: { value: 0 }, // 'change' is now calculated in the template
  strategies: []
});
const loading = ref(false);
const lastUpdated = ref(null);
const route = useRoute()
const store = useStore()
const tradeIds = ref([])
const motivationalImageUrl = ref('');
const isImageVisible = ref(false);

const handleScroll = () => {
  isImageVisible.value = window.scrollY > 50;
};

const fetchPortfolioData = async () => {
  loading.value = true;
  try {

    const response = await store.dispatch('portfolio/getLastPortfolioTrade');

    if (response.portoflioTradeDatail) {
      const info = response.portoflioTradeDatail.porfolioInfo;
      const trades = response.portoflioTradeDatail.tradeDetailList || [];

      portfolioData.value.name = info.portfolioName;
      portfolioData.value.pnl = info.pnl;
      const startDate = formatDate(info.portfolioStart);
      portfolioData.value.status = `(${startDate} -- ${info.isActive ? 'Active' : 'Inactive'})`;

      // ⬇️ --- SIMPLIFIED LOGIC --- ⬇️
      // The NIFTY display is now handled by the NiftyLevel component.
      // We only need to store the portfolio's start level.
      portfolioData.value.startLevel = { value: info.niftyLevelAtStart };

      portfolioData.value.strategies = trades.map(trade => ({
        name: trade.strategyName,
        type: trade.strategyType,
        pnl: trade.pnl || 0,
        positions: (trade.positionInfoList || []).map(pos => ({
          ticker: pos.ticker,
          type: pos.transactionType === 'BUY' ? 'Buy' : 'Sell',
          qty: pos.quantity,
          buyPrice: pos.avgPrice,
          ltp: pos.ltp,
          pnl: pos.pnl || 0
        }))
      }));
      lastUpdated.value = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
  } catch (error) {
    console.error("Failed to fetch and process portfolio data:", error);
    portfolioData.value.name = 'Error Loading Data';
  } finally {
    loading.value = false;
  }
};

const refreshPnL = async () => {
  try {
    loading.value = true
    await store.dispatch('positions/refreshLtpForPositions')
    await store.dispatch('trades/updateTradeListPnL', { tradeIds: tradeIds.value })
    await fetchPortfolioData()
  } catch (error) {
    console.error('Failed to refresh:', error)
  } finally {
    loading.value = false
  }
}

// --- Lifecycle Hooks ---
onMounted(async () => {
  window.addEventListener('scroll', handleScroll);

  fetchPortfolioData();
  try {
    const response = await store.dispatch('utils/getMotivationalImageUrl');
    if (response && response.imgUrl) {
      motivationalImageUrl.value = response.imgUrl;
    }
  } catch (error) {
    console.error("Failed to fetch motivational image:", error);
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

// --- Helper Functions ---
const getColorClass = (value) => {
  return Number(value) >= 0 ? 'text-green-lighten-1' : 'text-red-lighten-1';
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: '2-digit', month: 'short', year: '2-digit' };
  return date.toLocaleDateString('en-GB', options).replace(/ /g, '-');
};

</script>

<style scoped>
/* Scoped styles ensure these classes only apply within this component */
.text-red-lighten-1 {
  color: #EF5350 !important;
}

.text-green-lighten-1 {
  color: #66BB6A !important;
}

.v-expansion-panel-title__overlay {
  display: none;
}

.motivation-card {
  max-width: 800px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
}

.motivation-card.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.responsive-image {
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}
</style>